import {
  can,
  INSPECTION_STATUSES,
  type InspectionSummary,
  type MeResponse,
  type ProjectSummary,
  REMARK_STATUSES,
  type RemarkSummary,
  SiteReportList,
  type SiteReportSnapshot,
  type SiteReviewContext,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../../layout/crumbs';
import { ApiError, api } from '../../../lib/api';
import { useMe } from '../../../lib/session';
import { Button } from '../../../ui/button';
import { ErrorBox, Spinner, toast } from '../../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../../ui/form';
import { Page, PageHeader, Panel } from '../../../ui/layout';
import { NewInspectionDialog } from '../../supervision/InspectionPage';
import { SITE_STATES } from '../model';
import type { OfflineScope } from '../offline/store';
import { PostingStatus } from '../PostingStatus';
import { ReportPhotos } from '../SitePhotosPage';
import { weatherSummary } from '../weather';
import {
  type DecisionAction,
  pendingDecision,
  prepareDecision,
  type ReviewInput,
  reviewContextResponse,
} from './model';
import { type ReviewRecord, ReviewStore } from './store';
import { sendReviewDecision } from './transport';

type Search = {
  project?: string;
  report?: string;
  state?: string;
  from?: string;
  to?: string;
  revision?: number;
};
const asError = (e: unknown) => (e instanceof Error ? e : new Error('Не удалось выполнить действие'));
const stateOptions = {
  queue: 'Ожидают решения',
  submitted: 'На проверке',
  verified: 'Проверены',
  returned: 'Возвращены',
  accepted: 'Приняты',
  rejected: 'Отклонены',
};

export function SiteReviewPage() {
  const me = useMe();
  const [scope, setScope] = useState<OfflineScope | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    let live = true;
    setScope(null);
    setError(null);
    void api<MeResponse>('/auth/me')
      .then((fresh) => {
        if (fresh.user.id !== me.user.id || fresh.tenant?.id !== me.tenant?.id || !fresh.tenant?.staff)
          throw new Error('Сессия изменилась или недоступна. Войдите под нужной учётной записью');
        let deviceId = localStorage.getItem('epc:site:device');
        if (!deviceId) {
          deviceId = crypto.randomUUID();
          localStorage.setItem('epc:site:device', deviceId);
        }
        if (live) setScope({ tenantId: fresh.tenant.id, userId: fresh.user.id, deviceId });
      })
      .catch((e) => {
        if (live) setError(asError(e));
      });
    return () => {
      live = false;
    };
  }, [me.user.id, me.tenant?.id]);
  if (!scope)
    return (
      <Page>
        <PageHeader title="Проверка отчётов" />
        {error ? (
          <>
            <ErrorBox error={error} />
            <a className="inline-flex min-h-11 items-center underline" href="/login">
              Перейти ко входу
            </a>
          </>
        ) : (
          <Spinner />
        )}
      </Page>
    );
  return <ReviewWorkspace key={`${scope.tenantId}:${scope.userId}`} scope={scope} me={me} />;
}

function ReviewWorkspace({ scope, me }: { scope: OfflineScope; me: MeResponse }) {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const go = (next: Search) => navigate({ to: '/site/review', search: next });
  useCrumbs([
    { label: 'Площадка', to: '/site' },
    { label: 'Проверка отчётов', to: '/site/review' },
  ]);
  if (search.report)
    return (
      <ReviewDetail
        key={search.report}
        id={search.report}
        revision={search.revision}
        scope={scope}
        back={() => go({ ...search, report: undefined, revision: undefined })}
        showRevision={(revision) => go({ ...search, revision })}
      />
    );
  return (
    <ReportQueue
      key={JSON.stringify([search.project, search.state, search.from, search.to])}
      scope={scope}
      me={me}
      search={search}
      go={go}
    />
  );
}

function ReportQueue({
  scope,
  me,
  search,
  go,
}: {
  scope: OfflineScope;
  me: MeResponse;
  search: Search;
  go: (s: Search) => unknown;
}) {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const filterKey = JSON.stringify([search.project, search.state, search.from, search.to]);
  const projects = useQuery({
    queryKey: ['site-review', scope.tenantId, scope.userId, 'projects'],
    queryFn: () => api<ProjectSummary[]>('/projects', { expectedIdentity: scope }),
    retry: false,
  });
  const q = useQuery({
    queryKey: ['site-review', scope.tenantId, scope.userId, 'queue', filterKey, cursors.at(-1)],
    queryFn: async () =>
      SiteReportList.parse(
        await api(`/site/projects/${search.project}/reports`, {
          query: {
            state: search.state ?? 'queue',
            dateFrom: search.from,
            dateTo: search.to,
            cursor: cursors.at(-1),
          },
          expectedIdentity: scope,
        }),
      ),
    enabled: Boolean(search.project),
    retry: false,
  });
  const available = projects.data?.filter((p) =>
    ['site.view', 'site.review', 'site.report'].some((permission) =>
      can(me.access, permission, {
        projectId: p.id,
        legalEntityId: p.legalEntityId,
        directionId: p.directionId,
        projectPath: [p.parentId],
      }),
    ),
  );
  return (
    <Page className="max-w-5xl">
      <Button
        className="mb-3 !h-11"
        variant="ghost"
        onClick={() => {
          window.location.href = '/site';
        }}
      >
        К площадке
      </Button>
      <PageHeader
        title="Проверка отчётов"
        subtitle="Проверка объёмов и принятие — два отдельных решения ПТО или руководителя проекта."
        actions={
          <Button
            className="!h-11"
            onClick={() => {
              void q.refetch();
            }}
          >
            Обновить список
          </Button>
        }
      />
      <Panel title="Отчёты проекта">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Проект проверки" htmlFor="review-project">
            <Select
              id="review-project"
              className="!h-11"
              value={search.project ?? ''}
              onChange={(e) => go({ ...search, project: e.target.value || undefined })}
            >
              <option value="">Выберите проект</option>
              {available?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Состояние отчётов" htmlFor="review-state">
            <Select
              id="review-state"
              className="!h-11"
              value={search.state ?? 'queue'}
              onChange={(e) => go({ ...search, state: e.target.value })}
            >
              {Object.entries(stateOptions).map(([v, title]) => (
                <option key={v} value={v}>
                  {title}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Смена с" htmlFor="review-from">
            <Input
              id="review-from"
              className="!h-11"
              type="date"
              value={search.from ?? ''}
              onChange={(e) => go({ ...search, from: e.target.value || undefined })}
            />
          </Field>
          <Field label="Смена по" htmlFor="review-to">
            <Input
              id="review-to"
              className="!h-11"
              type="date"
              value={search.to ?? ''}
              onChange={(e) => go({ ...search, to: e.target.value || undefined })}
            />
          </Field>
        </div>
      </Panel>
      {projects.error ? <ErrorBox error={projects.error} /> : null}
      {q.error ? <ErrorBox error={q.error} /> : null}
      {search.project && q.isPending ? <Spinner /> : null}
      {!search.project ? (
        <p className="mt-5 text-sm text-ink-2">Выберите проект, чтобы увидеть отправленные отчёты.</p>
      ) : null}
      {q.data && !q.error ? (
        <div className="mt-5 grid gap-4">
          {q.data.items.length === 0 ? <p>В выбранном периоде нет отчётов с этим состоянием.</p> : null}
          {q.data.items.map((r) => (
            <Panel key={r.id} title={r.packageName} meta={SITE_STATES[r.state]}>
              <p className="text-sm">
                {r.reportDay} · {r.shiftKey} · версия {r.revision}
              </p>
              <p className="mt-1 text-sm text-ink-2">Автор: {r.authorName}</p>
              <Button className="mt-3 !h-11" onClick={() => go({ ...search, report: r.id })}>
                Открыть отчёт
              </Button>
            </Panel>
          ))}
          <div className="flex flex-wrap gap-3">
            <Button
              className="!h-11"
              disabled={cursors.length === 1}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              Предыдущая страница
            </Button>
            <Button
              className="!h-11"
              disabled={!q.data.nextCursor}
              onClick={() => setCursors((c) => [...c, q.data!.nextCursor!])}
            >
              Следующая страница
            </Button>
          </div>
        </div>
      ) : null}
    </Page>
  );
}

function ReviewDetail({
  id,
  revision,
  scope,
  back,
  showRevision,
}: {
  id: string;
  revision?: number;
  scope: OfflineScope;
  back: () => unknown;
  showRevision: (r?: number) => unknown;
}) {
  const q = useQuery({
    queryKey: ['site-review', scope.tenantId, scope.userId, 'context', id, revision],
    queryFn: async () => {
      const c = reviewContextResponse.parse(
        await api(`/site/reports/${id}/review-context`, { query: { revision }, expectedIdentity: scope }),
      );
      if (c.report.id !== id || c.report.tenantId !== scope.tenantId)
        throw new Error('Ответ относится к другому отчёту');
      return c as SiteReviewContext;
    },
    retry: false,
  });
  const c = q.data;
  const posting = useQuery({
    queryKey: ['site-review', scope.tenantId, scope.userId, 'posting', id, c?.report.revision],
    queryFn: () =>
      api<{ schedule: string; economy: string }>(`/site/reports/${id}/posting-status`, {
        expectedIdentity: scope,
      }),
    enabled: c?.report.state === 'accepted',
    retry: false,
  });
  return (
    <Page className="max-w-5xl">
      <Button className="mb-3 !h-11" variant="ghost" onClick={() => back()}>
        К очереди отчётов
      </Button>
      <PageHeader
        title="Проверка отчёта смены"
        subtitle={
          c ? `${c.projectName} · ${c.report.draft.reportDay} · ${c.report.draft.shiftKey}` : undefined
        }
        actions={
          <Button
            className="!h-11"
            onClick={() => {
              void q.refetch();
              void posting.refetch();
            }}
          >
            Обновить отчёт
          </Button>
        }
      />
      {q.error ? <ErrorBox error={q.error} /> : null}
      {q.isPending ? <Spinner /> : null}
      {q.error instanceof ApiError &&
      (q.error.status === 401 || q.error.code === 'session_context_changed') ? (
        <a className="inline-flex min-h-11 items-center underline" href="/login">
          Войти под исходным пользователем
        </a>
      ) : null}
      {c ? (
        <div className={q.error ? 'hidden' : 'grid gap-5'}>
          <Panel title={SITE_STATES[c.report.state]} meta={`Версия ${c.report.revision}`}>
            <p className="font-semibold">{c.packageName}</p>
            <p className="mt-2 text-sm">
              Автор: {c.authorName}. Часовой пояс смены: {c.report.draft.projectTimeZone}.
            </p>
            {c.decisionActorName ? (
              <p className="mt-2 text-sm">Последнее действие: {c.decisionActorName}</p>
            ) : null}
            {c.report.decision?.reason ? (
              <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                Причина: {c.report.decision.reason}
              </p>
            ) : null}
            <p className="mt-3 text-sm text-ink-2">
              Названия работ и пакета показаны по текущим справочникам. Количества и ресурсы относятся к
              выбранной версии отчёта.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                className="!h-11"
                disabled={c.report.revision <= 1}
                onClick={() => showRevision(c.report.revision - 1)}
              >
                Предыдущая версия
              </Button>
              <Button
                className="!h-11"
                disabled={c.report.revision >= c.currentRevision}
                onClick={() => showRevision(c.report.revision + 1)}
              >
                Следующая версия
              </Button>
              {revision !== undefined ? (
                <Button className="!h-11" onClick={() => showRevision(undefined)}>
                  Текущая версия
                </Button>
              ) : null}
            </div>
          </Panel>
          {c.restrictions.map((r) => (
            <p key={r.code} className="rounded-md border border-line bg-surface-2 p-4 text-sm" role="status">
              {r.message}
            </p>
          ))}
          {revision === undefined &&
          (c.capabilities.verify ||
            c.capabilities.return ||
            c.capabilities.accept ||
            c.report.authorId !== scope.userId) ? (
            <DecisionForm
              key={id}
              context={c}
              scope={scope}
              available={!q.error && !q.isFetching}
              refresh={async () => {
                await q.refetch();
              }}
            />
          ) : (
            <ReportLines context={c} />
          )}
          {c.report.state === 'accepted' ? (
            <Panel title="Отражение принятого отчёта">
              <PostingStatus data={posting.error ? undefined : posting.data} loading={posting.isPending} />
              {posting.error ? <ErrorBox error={posting.error} /> : null}
              <p className="mt-3 text-sm text-ink-2">
                Склад и ресурсный учёт ещё не подключены. Принятие не заменяет отдельное согласование оценки
                затрат.
              </p>
            </Panel>
          ) : null}
          {c.capabilities.reopen && revision === undefined ? (
            <ReopenPanel
              key={`${id}:${c.report.revision}`}
              context={c}
              scope={scope}
              done={async () => {
                await q.refetch();
              }}
            />
          ) : null}
          <p className="text-sm text-ink-2">
            Принятый отчёт автор не меняет. Исправить принятый факт может руководитель проекта: «Открыть
            снова» с причиной снимает факт в графике и затраты, отчёт возвращается автору.
          </p>
        </div>
      ) : null}
    </Page>
  );
}

/**
 * «Открыть снова» принятый отчёт (ПР-19): руководитель проекта указывает причину; факт графика и затраты этой версии
 * снимаются, отчёт возвращается автору. Повтор нажатия идёт с тем же ключом — второго снятия не будет.
 */
function ReopenPanel({
  context,
  scope,
  done,
}: {
  context: SiteReviewContext;
  scope: OfflineScope;
  done: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  // Ключ и время решения постоянны: повтор после потерянного ответа — та же команда, сервер вернёт сохранённый ответ
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString());
  const [sentReason, setSentReason] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const report = context.report;
  async function reopen() {
    if (!window.confirm('Открыть принятый отчёт снова? Факт в графике и затраты по нему будут сняты.'))
      return;
    setBusy(true);
    setError(null);
    // Причина фиксируется с первой попытки: её правка после отправки уходила бы под тем же ключом другим текстом
    if (sentReason === null) setSentReason(reason.trim());
    try {
      await api(`/site/reports/${report.id}/reopen`, {
        method: 'POST',
        body: {
          schemaVersion: 1,
          clientId: report.clientId,
          clientOperationId: key,
          expectedRevision: report.revision,
          recordedOffline: false,
          sourceRevisions: context.sourceRevisions,
          occurredAt,
          effectiveAt: report.draft.reportDay,
          reason: sentReason ?? reason.trim(),
        },
        idempotencyKey: key,
        expectedIdentity: scope,
      });
      toast('Отчёт открыт снова и возвращён автору');
      await done();
    } catch (e) {
      // Окончательный отказ сервера (склад, бюджет, права) повтором не исправить: форма снова открыта для новой попытки
      // с новым ключом. Сбой сети, вход и перегрузка — повтор той же командой
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && ![401, 408, 429].includes(e.status)) {
        setSentReason(null);
        setKey(crypto.randomUUID());
        setOccurredAt(new Date().toISOString());
      }
      setError(e instanceof Error ? e : new Error('Не удалось открыть отчёт снова'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel title="Открыть снова">
      <Field label="Причина" htmlFor="reopen-reason" hint="Автор увидит её в отчёте">
        <Textarea
          id="reopen-reason"
          maxLength={2000}
          disabled={sentReason !== null}
          value={sentReason ?? reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Что неверно в принятом отчёте"
        />
      </Field>
      {error ? <ErrorBox error={error} /> : null}
      <Button
        className="mt-3 !h-11"
        disabled={busy || reason.trim().length < 3 || !navigator.onLine}
        loading={busy}
        onClick={() => void reopen()}
      >
        Открыть снова
      </Button>
    </Panel>
  );
}

function DecisionForm({
  context,
  scope,
  available,
  refresh,
}: {
  context: SiteReviewContext;
  scope: OfflineScope;
  available: boolean;
  refresh: () => Promise<void>;
}) {
  const [store, setStore] = useState<ReviewStore | null>(null);
  const [row, setRow] = useState<ReviewRecord | null>(null);
  const rowRef = useRef<ReviewRecord | null>(null);
  const [input, setInput] = useState<ReviewInput | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(0);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const serial = useRef<Promise<unknown>>(Promise.resolve());
  const initialContext = useRef(context);
  const live = useRef(true);
  const unsaved = saving > 0 || Boolean(input && row && JSON.stringify(input) !== JSON.stringify(row.input));
  useBlocker({
    shouldBlockFn: () =>
      unsaved && !window.confirm('Проверка ещё не сохранена на устройстве. Уйти и потерять этот ввод?'),
    enableBeforeUnload: unsaved,
  });
  const apply = (next: ReviewRecord) => {
    rowRef.current = next;
    if (live.current) setRow(next);
    return next;
  };
  useEffect(() => {
    live.current = true;
    let cancelled = false;
    let opened: ReviewStore | undefined;
    void ReviewStore.open()
      .then(async (s) => {
        opened = s;
        if (cancelled) {
          s.close();
          return;
        }
        const old = await s.read(scope, initialContext.current.report.id);
        if (cancelled) {
          s.close();
          return;
        }
        const loaded = old ?? (await s.start(scope, initialContext.current, 0));
        if (!cancelled) {
          setStore(s);
          rowRef.current = loaded;
          setRow(loaded);
          setInput(loaded.input);
        } else s.close();
      })
      .catch((e) => {
        if (!cancelled) setError(asError(e));
      });
    return () => {
      cancelled = true;
      live.current = false;
      opened?.close();
    };
  }, [scope]);
  useEffect(() => {
    const change = () => setOnline(navigator.onLine);
    window.addEventListener('online', change);
    window.addEventListener('offline', change);
    return () => {
      window.removeEventListener('online', change);
      window.removeEventListener('offline', change);
    };
  }, []);
  function enqueue(task: () => Promise<void>) {
    const next = serial.current.then(task);
    serial.current = next.catch((e) => {
      if (live.current) setError(asError(e));
    });
    return serial.current;
  }
  function change(next: ReviewInput) {
    if (!store || busy || rowRef.current?.command) return;
    setInput(next);
    setSaving((n) => n + 1);
    void enqueue(async () => {
      try {
        const current = rowRef.current!;
        apply(await store.save(scope, context.report.id, current.localRevision, next));
      } finally {
        if (live.current) setSaving((n) => n - 1);
      }
    });
  }
  async function send(action?: DecisionAction) {
    if (!store || busy || !available) return;
    setBusy(true);
    setError(null);
    await enqueue(async () => {
      let current = rowRef.current!;
      if (action) {
        if (
          current.context.report.revision !== context.report.revision ||
          JSON.stringify(current.input) !== JSON.stringify(input)
        )
          throw new Error('Сначала сохраните ввод и начните проверку текущей версии');
        const command = prepareDecision(context, scope, current.input, action, navigator.onLine);
        current = apply(await store.begin(scope, context.report.id, current.localRevision, command));
      }
      if (!navigator.onLine) throw new Error('Для повторения решения подключитесь к сети');
      const completed = await sendReviewDecision(current, scope);
      apply(await store.finish(scope, context.report.id, completed));
      await refresh();
    });
    if (live.current) setBusy(false);
  }
  async function restart() {
    if (!store || busy || pendingDecision(row?.command ?? null)) return;
    if (!window.confirm('Заменить сохранённый ввод и начать проверку текущей версии отчёта?')) return;
    setBusy(true);
    setError(null);
    await enqueue(async () => {
      const fresh = reviewContextResponse.parse(
        await api(`/site/reports/${context.report.id}/review-context`, { expectedIdentity: scope }),
      ) as SiteReviewContext;
      const next = apply(await store.start(scope, fresh, rowRef.current!.localRevision));
      setInput(next.input);
      await refresh();
    });
    if (live.current) setBusy(false);
  }
  if (!row || !input)
    return (
      <>
        {error ? (
          <>
            <ErrorBox error={error} />
            <ReportLines context={context} />
          </>
        ) : (
          <Spinner />
        )}
      </>
    );
  const sameRevision = row.context.report.revision === context.report.revision;
  const editable =
    available &&
    sameRevision &&
    !row.command &&
    !busy &&
    (context.capabilities.verify || context.capabilities.return);
  return (
    <>
      <p className="text-sm text-ink-2" role="status">
        {unsaved ? 'Ввод ещё не сохранён на устройстве' : 'Ввод проверки сохранён на этом устройстве'}
        {!online ? ' · Нет сети: новое решение недоступно' : ''}
      </p>
      {error ? <ErrorBox error={error} /> : null}
      {!sameRevision ? (
        <p className="rounded-md border border-line p-4 text-sm">
          Сохранённая проверка относится к версии {row.context.report.revision}. На сервере версия{' '}
          {context.report.revision}. Введённые значения оставлены для сравнения.
        </p>
      ) : null}
      <ReportLines
        context={context}
        input={sameRevision && context.capabilities.verify ? input : undefined}
        editable={editable && context.capabilities.verify}
        change={change}
      />
      {!sameRevision && row.command?.state !== 'done' ? (
        <Panel title={`Сохранённый ввод проверки версии ${row.context.report.revision}`}>
          <p className="mb-3 text-sm text-ink-2">
            Это ваш локальный ввод по прежним работам. Текущая версия отчёта показана выше.
          </p>
          <ReportLines context={row.context} input={input} />
        </Panel>
      ) : null}
      {row.command ? (
        <Panel title={row.command.state === 'done' ? 'Решение подтверждено сервером' : 'Состояние решения'}>
          <p className="text-sm" role="status">
            {row.command.error ??
              (row.command.state === 'done'
                ? 'Сохранённый результат получен. Следующее действие выполняется отдельно.'
                : 'Ожидается подтверждение сервера')}
          </p>
          {pendingDecision(row.command) ? (
            <Button
              className="mt-3 !h-11"
              disabled={busy || !online || !available}
              onClick={() => {
                void send();
              }}
            >
              Повторить то же решение
            </Button>
          ) : null}
          {row.command.state === 'auth_paused' ? (
            <a className="mt-2 flex min-h-11 items-center underline" href="/login">
              Войти под исходным пользователем
            </a>
          ) : null}
        </Panel>
      ) : null}
      {context.capabilities.return && !row.command ? (
        <Field label="Причина возврата" htmlFor="review-return-reason">
          <Textarea
            id="review-return-reason"
            maxLength={2000}
            disabled={!editable}
            value={input.returnReason}
            onChange={(e) => change({ ...input, returnReason: e.target.value })}
            placeholder="Что необходимо уточнить автору"
          />
        </Field>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {context.capabilities.verify && !row.command ? (
          <Button
            variant="primary"
            className="!h-11"
            disabled={busy || unsaved || !sameRevision || !online || !available}
            onClick={() => {
              void send('verify');
            }}
          >
            Подтвердить проверку
          </Button>
        ) : null}
        {context.capabilities.accept && !row.command ? (
          <Button
            variant="primary"
            className="!h-11"
            disabled={busy || unsaved || !sameRevision || !online || !available}
            onClick={() => {
              void send('accept');
            }}
          >
            Принять отчёт
          </Button>
        ) : null}
        {context.capabilities.return && !row.command ? (
          <Button
            className="!h-11"
            disabled={busy || unsaved || !sameRevision || !online || !available}
            onClick={() => {
              void send('return');
            }}
          >
            Вернуть автору
          </Button>
        ) : null}
        {!pendingDecision(row.command) &&
        (context.capabilities.verify || context.capabilities.accept || context.capabilities.return) &&
        (row.command || !sameRevision || error) ? (
          <Button
            className="!h-11"
            disabled={busy || !online || !available}
            onClick={() => {
              void restart();
            }}
          >
            Начать проверку текущей версии
          </Button>
        ) : null}
      </div>
      {context.capabilities.accept ? (
        <p className="text-sm text-ink-2">
          Принятие запишет проверенные объёмы в график. Оценка затрат выполняется отдельно. После принятия
          отчёт закрыт для изменений.
        </p>
      ) : null}
    </>
  );
}

function ReportLines({
  context,
  input,
  editable = false,
  change,
}: {
  context: SiteReviewContext;
  input?: ReviewInput;
  editable?: boolean;
  change?: (v: ReviewInput) => void;
}) {
  const r = context.report;
  return (
    <>
      {r.draft.lines.map((line, i) => {
        const value = input?.lines.find((v) => v.lineId === line.id);
        const update = (patch: Partial<ReviewInput['lines'][number]>) => {
          if (input && change)
            change({
              ...input,
              lines: input.lines.map((v) => (v.lineId === line.id ? { ...v, ...patch } : v)),
            });
        };
        return (
          <Panel
            key={line.id}
            title={`${i + 1}. ${context.lines.find((n) => n.lineId === line.id)?.name ?? 'Работа'}`}
          >
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-ink-2">Заявлено</dt>
                <dd>
                  {line.claimedQuantity} {line.unit}
                </dd>
              </div>
              <div>
                <dt className="text-ink-2">Проверено</dt>
                <dd>{r.verification?.find((v) => v.lineId === line.id)?.quantity ?? 'Ещё не проверено'}</dd>
              </div>
              <div>
                <dt className="text-ink-2">Принято</dt>
                <dd>{r.accepted?.find((v) => v.lineId === line.id)?.quantity ?? 'Ещё не принято'}</dd>
              </div>
            </dl>
            {line.note ? (
              <p className="mt-3 whitespace-pre-wrap break-words text-sm">Комментарий автора: {line.note}</p>
            ) : null}
            <ReportPhotos report={r} lineId={line.id} />
            {r.verification?.find((v) => v.lineId === line.id)?.reason ? (
              <p className="mt-3 whitespace-pre-wrap break-words text-sm">
                Причина изменения: {r.verification.find((v) => v.lineId === line.id)!.reason}
              </p>
            ) : null}
            <Resources line={line} />
            {line.stepIds.length ? (
              <p className="mt-3 text-sm">
                В основании выбрано шагов отчёта: {line.stepIds.length}. Принятие не закрывает акты и
                контрольные точки.
              </p>
            ) : (
              <p className="mt-3 text-sm text-ink-2">Шаги выполнения в отчёте не выбраны.</p>
            )}
            {value ? (
              <div className="mt-4 grid gap-3">
                <Field
                  label={`Проверенный объём, работа ${i + 1} (${line.unit})`}
                  htmlFor={`verify-${line.id}`}
                >
                  <Input
                    id={`verify-${line.id}`}
                    className="!h-11"
                    inputMode="decimal"
                    value={value.quantity}
                    disabled={!editable}
                    onChange={(e) => update({ quantity: e.target.value })}
                  />
                </Field>
                <Field label={`Причина уменьшения, работа ${i + 1}`} htmlFor={`reason-${line.id}`}>
                  <Textarea
                    id={`reason-${line.id}`}
                    maxLength={2000}
                    value={value.reason}
                    disabled={!editable}
                    onChange={(e) => update({ reason: e.target.value })}
                  />
                </Field>
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 shrink-0"
                    disabled={!editable}
                    checked={value.resourcesConfirmed}
                    onChange={(e) => update({ resourcesConfirmed: e.target.checked })}
                  />
                  Ресурсы работы {i + 1} проверены
                  {!line.labor.length && !line.machines.length && !line.materials.length
                    ? ' — в отчёте не указаны'
                    : ''}
                </label>
              </div>
            ) : null}
            <Remarks report={r} lineId={line.id} />
            <HiddenWorks
              report={r}
              activityId={line.activityId}
              workName={context.lines.find((n) => n.lineId === line.id)?.name ?? ''}
            />
          </Panel>
        );
      })}
      {weatherSummary(r.draft.weather) ? (
        <Panel title="Погода">
          <p className="break-words text-sm">{weatherSummary(r.draft.weather)}</p>
        </Panel>
      ) : null}
      {r.draft.note ? (
        <Panel title="Комментарий к смене">
          <p className="whitespace-pre-wrap break-words text-sm">{r.draft.note}</p>
        </Panel>
      ) : null}
    </>
  );
}

function Resources({ line }: { line: SiteReportSnapshot['draft']['lines'][number] }) {
  return (
    <div className="mt-4 rounded-md bg-surface-2 p-3 text-sm">
      <h3 className="font-semibold">Ресурсы по отчёту</h3>
      {!line.labor.length && !line.machines.length && !line.materials.length ? (
        <p className="mt-2">Ресурсы не указаны.</p>
      ) : null}
      {line.labor.map((l) => (
        <p key={l.id} className="mt-2 break-words">
          {l.profession}: {l.headcount} чел., всего {l.hours} чел.-ч, из них простой {l.idleHours}
          {l.idleReason ? ` — ${l.idleReason}` : ''}
        </p>
      ))}
      {line.machines.map((m, i) => (
        <p key={m.id} className="mt-2 break-words">
          Техника {i + 1}: {m.hours} ч работы, {m.idleHours} ч простоя
          {m.idleReason ? ` — ${m.idleReason}` : ''}. ID: {m.machineId}
        </p>
      ))}
      {line.materials.map((m, i) => (
        <p key={m.id} className="mt-2 break-words">
          Материал {i + 1}: {m.quantity} {m.unit}. Склад {m.warehouseId}, позиция {m.itemId}
        </p>
      ))}
      <p className="mt-2 text-ink-2">
        При принятии материалы списываются со склада; часы с выбранным работником — в табель.
      </p>
    </div>
  );
}

function Remarks({ report, lineId }: { report: SiteReportSnapshot; lineId: string }) {
  const me = useMe();
  const [opened, setOpened] = useState(false);
  const q = useQuery({
    queryKey: ['site-review-remarks', report.tenantId, me.user.id, report.id, report.revision, lineId],
    queryFn: () =>
      api<{
        links: Array<{ linkId: string; remark: Pick<RemarkSummary, 'id' | 'number' | 'title' | 'status'> }>;
        count: number;
      }>('/quality/site-links', {
        query: { reportId: report.id, reportRevision: report.revision, lineId },
        expectedIdentity: { tenantId: report.tenantId, userId: me.user.id },
      }),
    enabled: opened,
    retry: false,
  });
  return (
    <div className="mt-3">
      <Button className="!h-11" variant="ghost" onClick={() => setOpened((v) => !v)}>
        {opened ? 'Скрыть замечания' : 'Доступные замечания'}
      </Button>
      {opened ? (
        <div className="mt-2 text-sm">
          {q.isPending ? <Spinner /> : null}
          {q.error ? (
            <p>
              {q.error instanceof ApiError && [403, 404].includes(q.error.status)
                ? 'Нет доступа к просмотру связанных замечаний.'
                : 'Не удалось прочитать замечания. Повторите позже.'}
            </p>
          ) : null}
          {q.data && !q.error ? (
            <>
              <p>Доступно связей: {q.data.count}. Список ограничен вашими правами.</p>
              {q.data.links.map((link) => (
                <Link
                  key={link.linkId}
                  className="mt-2 flex min-h-11 items-center break-words underline"
                  to="/supervision/remarks/$remarkId"
                  params={{ remarkId: link.remark.id }}
                >
                  {link.remark.number}: {link.remark.title} · {REMARK_STATUSES[link.remark.status]}
                </Link>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Скрытые работы по строке отчёта: уже сделанные вызовы на освидетельствование и новый вызов (ПР-10) */
function HiddenWorks({
  report,
  activityId,
  workName,
}: {
  report: SiteReportSnapshot;
  activityId: string;
  workName: string;
}) {
  const me = useMe();
  const [dialog, setDialog] = useState(false);
  const canCall = can(me.access, 'supervision.respond', { projectId: report.draft.projectId });
  const q = useQuery({
    queryKey: ['inspections', 'by-report', report.id],
    queryFn: () => api<InspectionSummary[]>('/supervision/inspections', { query: { reportId: report.id } }),
    enabled: report.state !== 'draft',
    retry: false,
  });
  const mine = (q.data ?? []).filter((x) => x.activityId === activityId);
  if (report.state === 'draft' || report.state === 'rejected') return null;
  return (
    <div className="mt-3">
      {mine.map((x) => (
        <Link
          key={x.id}
          className="flex min-h-11 items-center break-words text-sm underline"
          to="/supervision/inspections/$inspectionId"
          params={{ inspectionId: x.id }}
        >
          {x.number}: {x.workName} · {INSPECTION_STATUSES[x.status]}
        </Link>
      ))}
      {canCall ? (
        <Button className="!h-11" variant="ghost" onClick={() => setDialog(true)}>
          Предъявить скрытые работы
        </Button>
      ) : null}
      {dialog ? (
        <NewInspectionDialog
          projectId={report.draft.projectId}
          preset={{ reportId: report.id, activityId, workName }}
          onClose={() => setDialog(false)}
        />
      ) : null}
    </div>
  );
}
